import { TrajectoryMetadataProvider } from '@trimble/web3d-providers';
import { SetStoreFunction } from 'solid-js/store';
import { useConnectProject } from '@trimble/connect-project-providers';
import { useApiServices } from '../ApiServiceProvider';
import { ProjectDataState, TrajectoryItem } from './DataStateModels';
import { resolveTrajectorySplittable } from './resolve-trajectory-splittable';
import { useProjectSettings } from '../ProjectSettingsProvider';
import {
  DataLoadingService as BaseDataLoadingService,
  DataLoadingServiceConfig,
  ProviderHandler,
} from '@trimble/data-loading';
import {
  createModelConfig,
  createPointCloudConfig,
  createStyledTrajectoryConfig,
  createTrimBimConfig,
} from './ProviderConfigurations';
import { Point2D } from '@trimble/utilities';
import {
  createPointCloudLoadErrorNotifier,
  PointCloudLoadErrorNotifier,
} from './point-cloud-load-error-notifier';

const POINT_CLOUD_LOAD_CONCURRENCY = 10;

/** Item types supported by the mobile-mapping service */
export type ItemType =
  | 'pointCloud'
  | 'trajectory'
  | 'styled-trajectory'
  | 'model';

export class DataLoadingService extends BaseDataLoadingService<
  ItemType,
  ProjectDataState
> {
  private readonly cancelPointCloudQueries: (
    pointCloudId?: string
  ) => Promise<void>;

  private readonly pointCloudLoadErrorNotifier: PointCloudLoadErrorNotifier;

  constructor(
    projectDataState: ProjectDataState,
    setProjectDataState: SetStoreFunction<ProjectDataState>,
    setLoadedCsib: (itemId: string, csib: string | undefined) => void,
    setLoadedCenter: (itemId: string, center: Point2D | undefined) => void
  ) {
    const { selectedProject, selectedTenantTrn, selectedRegion } =
      useConnectProject();
    const { scanDataApi, alignmentApi, tcTcpsApi, crsResolver } =
      useApiServices();
    const { formattingSettings, alignmentSettings } = useProjectSettings();

    const getTrajectoryMetadataProvider = ():
      | TrajectoryMetadataProvider
      | undefined =>
      this.providers().find(
        (prov): prov is TrajectoryMetadataProvider =>
          prov instanceof TrajectoryMetadataProvider
      );

    const getAllTrajectoryMetadataProviders =
      (): TrajectoryMetadataProvider[] =>
        this.providers().filter(
          (prov): prov is TrajectoryMetadataProvider =>
            prov instanceof TrajectoryMetadataProvider
        );

    const resolveSplittable = (
      id: string,
      signal?: AbortSignal
    ): Promise<TrajectoryItem | undefined> =>
      resolveTrajectorySplittable(projectDataState, id, signal);

    const trajectoryHandler = new ProviderHandler(
      // Trajectory geometry loads through TrimBimCapabilityProvider; API type is still `trajectory`.
      createTrimBimConfig(
        alignmentApi,
        selectedTenantTrn,
        formattingSettings,
        getTrajectoryMetadataProvider,
        getAllTrajectoryMetadataProviders,
        alignmentSettings,
        resolveSplittable
      ),
      projectDataState,
      setProjectDataState
    );

    const styledTrajectoryHandler = new ProviderHandler(
      createStyledTrajectoryConfig(
        formattingSettings,
        getTrajectoryMetadataProvider,
        getAllTrajectoryMetadataProviders,
        alignmentSettings,
        projectDataState
      ),
      projectDataState,
      setProjectDataState
    );

    const pointCloudLoadErrorNotifier = createPointCloudLoadErrorNotifier();

    const pointCloudHandler = new ProviderHandler(
      createPointCloudConfig(
        scanDataApi,
        crsResolver,
        selectedRegion,
        selectedProject,
        setLoadedCsib,
        setLoadedCenter,
        () => pointCloudLoadErrorNotifier.report()
      ),
      projectDataState,
      setProjectDataState
    );

    const modelHandler = new ProviderHandler(
      createModelConfig(
        tcTcpsApi,
        selectedRegion,
        getTrajectoryMetadataProvider,
        getAllTrajectoryMetadataProviders
      ),
      projectDataState,
      setProjectDataState
    );

    const dataLoadingServiceConfig: DataLoadingServiceConfig<
      ItemType,
      ProjectDataState
    > = {
      dataState: projectDataState,
      setDataState: setProjectDataState,
      handlers: [
        {
          type: 'trajectory',
          handler: trajectoryHandler,
          queueKey: 'trajectory',
          sliceKey: 'trajectory',
        },
        {
          type: 'pointCloud',
          handler: pointCloudHandler,
          queueKey: 'pointCloud',
          sliceKey: 'pointCloud',
        },
        {
          type: 'model',
          handler: modelHandler,
          queueKey: 'model',
          sliceKey: 'tcFileFolder',
        },
      ],
      transformHandlers: [
        {
          type: 'styled-trajectory',
          handler: styledTrajectoryHandler,
          queueKey: 'trajectory',
          sliceKey: 'trajectory',
        },
      ],
      queueConcurrency: {
        pointCloud: POINT_CLOUD_LOAD_CONCURRENCY,
      },
    };

    super(dataLoadingServiceConfig);

    this.cancelPointCloudQueries = (pointCloudId?: string) =>
      scanDataApi.cancelPointCloudQueries(pointCloudId);
    this.pointCloudLoadErrorNotifier = pointCloudLoadErrorNotifier;
  }

  public override unload(type: ItemType, itemId: string): void {
    if (type === 'pointCloud') {
      void this.cancelPointCloudQueries(itemId);
    }
    super.unload(type, itemId);
  }

  public cancelAllLoads(): void {
    this.cancelAllOperations();
    void this.cancelPointCloudQueries();
    this.pointCloudLoadErrorNotifier.cancel();
  }
}
